import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { toast } from "sonner";
import { useOrganization } from "@/hooks/use-organization";
import { useTRPC } from "@/trpc";
import { useOrganizationWageSetMutationHandlers } from "../hooks/use-organization-wage-set-mutations";
import { getTodayInTimezone, isCalendarDate } from "../utils/wage-dates";
import { DateField } from "./date-field";
import { OrganizationWageSetDialog } from "./organization-wage-set-dialog";

type EndAssignmentDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  assignmentId: string;
  assigneeName: string;
};

export function EndAssignmentDialog({
  open,
  onOpenChange,
  assignmentId,
  assigneeName,
}: EndAssignmentDialogProps) {
  const trpc = useTRPC();
  const { selectedOrgBusinessUnit } = useOrganization();
  const { invalidateOrganizationWageSets, handleError } =
    useOrganizationWageSetMutationHandlers();
  const [lastDay, setLastDay] = useState(() =>
    getTodayInTimezone({ timezoneId: selectedOrgBusinessUnit.timezoneId }),
  );
  const [dateError, setDateError] = useState<string>();

  const endMutation = useMutation(
    trpc.organizationWageSet.endAssignment.mutationOptions({
      onSuccess: async () => {
        await invalidateOrganizationWageSets();
        toast.success("Assignment ended");
        onOpenChange(false);
      },
      onError: handleError,
    }),
  );

  function handleSubmit() {
    if (!isCalendarDate(lastDay)) {
      setDateError("Choose a last day");
      return;
    }
    setDateError(undefined);
    endMutation.mutate({
      organizationBusinessUnitId: selectedOrgBusinessUnit.id,
      assignmentId,
      lastDay,
    });
  }

  return (
    <OrganizationWageSetDialog
      open={open}
      onOpenChange={onOpenChange}
      title="End assignment"
      description={`This wage set stops applying to ${assigneeName} after the last day.`}
      submitLabel="End assignment"
      isPending={endMutation.isPending}
      onSubmit={handleSubmit}
    >
      <DateField
        label="Last day"
        value={lastDay}
        onChange={setLastDay}
        error={dateError}
      />
    </OrganizationWageSetDialog>
  );
}
